import { Scene, GameObjects } from 'phaser';
import * as Phaser from 'phaser';
import { initLocalization, t } from '../i18n/Localization';
import { audioManager } from '../core/CoreManagerAudio';
import UIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';

export class GameOver extends Scene {
  camera: Phaser.Cameras.Scene2D.Camera;
  background: Phaser.GameObjects.Image;
  gameover_text: Phaser.GameObjects.Text;
  override rexUI!: UIPlugin;
  levelNumber: number = 0;
  biomatter: number = 0;

  constructor() {
    super('GameOver');
  }

  create(data?: any) {
    initLocalization(this);
    
    // Get optional data passed from the level
    if (data) {
      this.levelNumber = data.levelNumber || 0;
      this.biomatter = data.biomatter || 0;
    }

    this.rexUI = (this as any).rexUI as UIPlugin;

    // Configure camera
    this.camera = this.cameras.main;
    this.camera.setBackgroundColor(0x0d1f33);

    // Fade in de entrada
    this.camera.setAlpha(0);
    this.tweens.add({
      targets: this.camera,
      alpha: 1,
      duration: 500,
      ease: 'Power2'
    });

    // Dark overlay background
    this.add.rectangle(this.scale.width / 2, this.scale.height / 2, this.scale.width, this.scale.height, 0x000000, 0.6);

    // Background image
    this.background = this.add.image(0, 0, 'background').setOrigin(0).setAlpha(0.3);

    // "Game Over" text – con animación
    this.gameover_text = this.add
      .text(0, 0, t('defeat.title'), {
        fontFamily: 'Arial Black',
        fontSize: '72px',
        color: '#ff4444',
        stroke: '#000000',
        strokeThickness: 8,
        align: 'center',
      })
      .setOrigin(0.5);
    this.gameover_text.setAlpha(0);

    // Información del juego (si existe)
    let infoTexts: Phaser.GameObjects.Text[] = [];
    if (this.levelNumber > 0) {
      const levelText = this.add.text(0, 0, `Nivel: ${this.levelNumber}`, {
        fontFamily: 'Arial',
        fontSize: '28px',
        color: '#ffffff',
        align: 'center'
      }).setOrigin(0.5);
      infoTexts.push(levelText);
      levelText.setAlpha(0);
    }

    if (this.biomatter > 0) {
      const biomatterText = this.add.text(0, 0, t('feedback.biomatter_collected').replace('{amount}', this.biomatter.toString()), {
        fontFamily: 'Arial',
        fontSize: '24px',
        color: '#44ff88',
        align: 'center'
      }).setOrigin(0.5);
      infoTexts.push(biomatterText);
      biomatterText.setAlpha(0);
    }

    // Create buttons
    const retryBtn = this.createButton(t('death.restart'), 0x4a9c6d, () => {
      if (this.levelNumber > 0) {
        this.scene.start('LevelPreloader', {
          levelId: `level${this.levelNumber}`,
          levelNum: this.levelNumber,
          weapons: {
            primary: 'laserSimple',
            secondary: null,
            special: 'tractorPuny',
          },
        });
      }
    });

    const menuBtn = this.createButton(t('ui.back_to_menu'), 0x8b3a3a, () => {
      audioManager.stopMusic();
      audioManager.playMusicForScene('MainMenu');
      this.scene.start('MainMenu');
    });

    // Initial responsive layout
    this.updateLayout(this.scale.width, this.scale.height, infoTexts, [retryBtn, menuBtn]);

    // Update layout on canvas resize
    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      const { width, height } = gameSize;
      this.updateLayout(width, height, infoTexts, [retryBtn, menuBtn]);
    });

    // Animate everything in
    this.tweens.add({
      targets: this.gameover_text,
      alpha: 1,
      y: { from: this.scale.height * 0.15, to: this.scale.height * 0.25 },
      duration: 500,
      ease: 'Back.easeOut'
    });

    infoTexts.forEach((text, idx) => {
      text.setAlpha(0);
      this.tweens.add({
        targets: text,
        alpha: 1,
        duration: 400,
        delay: 200 + idx * 100,
        ease: 'Power2'
      });
    });

    [retryBtn, menuBtn].forEach((btn, idx) => {
      btn.setAlpha(0);
      this.tweens.add({
        targets: btn,
        alpha: 1,
        duration: 400,
        delay: 400 + idx * 100,
        ease: 'Power2'
      });
    });

    // Play game over music/sound
    audioManager.stopMusic();
    audioManager.playUISound('click', this); // Sonido de "game over"
  }

  private createButton(text: string, bgColor: number, onClick: () => void): GameObjects.Container {
    const container = this.add.container(0, 0);
    const bg = this.rexUI.add.roundRectangle(0, 0, 200, 60, 12, bgColor)
      .setStrokeStyle(3, 0xffffff);
    const label = this.add.text(0, 0, text, {
      fontFamily: 'Arial Black',
      fontSize: '24px',
      color: '#ffffff'
    }).setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(200, 60);
    container.setInteractive(
      new Phaser.Geom.Rectangle(-100, -30, 200, 60),
      Phaser.Geom.Rectangle.Contains
    );

    const normalColor = bgColor;
    const hoverColor = Phaser.Display.Color.ValueToColor(bgColor).lighten(20).color;
    const downColor = Phaser.Display.Color.ValueToColor(bgColor).darken(20).color;

    let isPressed = false;

    container.on('pointerover', () => {
      if (isPressed) return;
      bg.setFillStyle(hoverColor);
      container.setScale(1.08);
      audioManager.playUISound('hover', this);
    });

    container.on('pointerout', () => {
      if (isPressed) return;
      bg.setFillStyle(normalColor);
      container.setScale(1);
    });

    container.on('pointerdown', () => {
      isPressed = true;
      bg.setFillStyle(downColor);
      container.setScale(0.95);
    });

    container.on('pointerup', () => {
      if (!isPressed) return;
      isPressed = false;
      audioManager.playUISound('click', this);
      onClick();
    });

    container.on('pointerupoutside', () => {
      isPressed = false;
      bg.setFillStyle(normalColor);
      container.setScale(1);
    });

    container.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!isPressed) return;
      const bounds = new Phaser.Geom.Rectangle(-100, -30, 200, 60);
      const isInBounds = Phaser.Geom.Rectangle.Contains(bounds, pointer.x - container.x, pointer.y - container.y);
      if (!isInBounds && isPressed) {
        isPressed = false;
        bg.setFillStyle(normalColor);
        container.setScale(1);
      }
    });

    return container;
  }

  private updateLayout(width: number, height: number, infoTexts: Phaser.GameObjects.Text[], buttons: GameObjects.Container[]): void {
    // Resize camera viewport
    this.cameras.resize(width, height);

    // Stretch background
    if (this.background) {
      this.background.setDisplaySize(width, height);
    }

    // Compute scale factor
    const scaleFactor = Math.min(Math.min(width / 1024, height / 768), 1);

    // Position title
    if (this.gameover_text) {
      this.gameover_text.setPosition(width / 2, height * 0.25);
      this.gameover_text.setScale(scaleFactor);
    }

    // Position info texts
    infoTexts.forEach((text, idx) => {
      text.setPosition(width / 2, height * 0.4 + idx * 50);
      text.setScale(scaleFactor * 0.9);
    });

    // Position buttons
    const buttonStartY = height * 0.65;
    buttons.forEach((btn, idx) => {
      const xOffset = idx === 0 ? -120 : 120;
      btn.setPosition(width / 2 + xOffset, buttonStartY);
      btn.setScale(scaleFactor);
    });
  }
}
